import { useState, useMemo, useCallback } from "react";
import { View, Text, FlatList, Pressable, Platform, Image } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  ChevronLeft,
  ChevronRight,
  Trophy,
  Users,
  Calendar,
} from "lucide-react-native";
import { useOnboarding } from "../../../onboarding/OnboardingContext";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { EMBER } from "../../../onboarding/theme";
import type { CostsStackParamList } from "../../types";
import { useRegion, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { MoonFilterChip, MoonSearchInput } from "@louise/moon-ui";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import EligibilityBadge from "./EligibilityBadge";
import GrantsFilterModal from "./GrantsFilterModal";
import { GRANTS } from "./grants.data";
import { filterGrants, checkGrantEligibility } from "./grants.utils";
import type { Grant, GrantsFilters, GrantTreatment } from "./grants.types";
import { useSmartBack } from "../../hooks/useSmartBack";
import { inferRegionCode } from "@src/packages/costs/estimates/locations";

type Props = NativeStackScreenProps<CostsStackParamList, "Grants">;

const QUICK_FILTERS: {
  label: string;
  treatment?: GrantTreatment;
  status?: "open";
}[] = [
  { label: "All" },
  { label: "Open Now", status: "open" },
  { label: "IVF", treatment: "IVF" },
  { label: "Egg Freezing", treatment: "Egg Freezing" },
  { label: "Donor Egg", treatment: "Donor Egg" },
  { label: "Surrogacy", treatment: "Surrogacy" },
];

export default function GrantsScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const region = useRegion();
  const capturePress = usePressCapture("GrantsScreen");
  const [activeChip, setActiveChip] = useState("All");
  const [filters, setFilters] = useState<GrantsFilters>({});
  const { data: onboarding } = useOnboarding();
  const { data: patient } = usePatient();
  const coverage = useCoverage();
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "CostsOverview",
  });

  const patientData = useMemo(
    () => ({
      state: inferRegionCode(
        patient?.addresses?.[0]?.country ?? onboarding?.country,
        patient?.addresses?.[0]?.postalCode ?? onboarding?.zipCode,
      ),
      age: onboarding?.age ?? null,
      hasFertilityCoverage: coverage?.fertilityCoverage?.hasCoverage ?? null,
    }),
    [
      onboarding?.country,
      onboarding?.zipCode,
      onboarding?.age,
      patient?.addresses,
      coverage?.fertilityCoverage?.hasCoverage,
    ],
  );

  const effectiveFilters = useMemo<GrantsFilters>(() => {
    const chip = QUICK_FILTERS.find((f) => f.label === activeChip);
    return {
      ...filters,
      ...(chip?.treatment ? { treatments: [chip.treatment] } : {}),
      ...(chip?.status ? { applicationStatus: [chip.status] } : {}),
    };
  }, [filters, activeChip]);

  const filteredGrants = useMemo(
    () => filterGrants(GRANTS, effectiveFilters, patientData),
    [effectiveFilters, patientData],
  );

  const eligibilityMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof checkGrantEligibility>>();
    for (const grant of GRANTS) {
      map.set(grant.id, checkGrantEligibility(grant, patientData));
    }
    return map;
  }, [patientData]);

  const handleChipPress = useCallback((label: string) => {
    setActiveChip(label);
  }, []);

  const handleFiltersChange = useCallback((newFilters: GrantsFilters) => {
    setFilters(newFilters);
    setActiveChip("All");
  }, []);

  const handleGrantPress = useCallback(
    (grant: Grant) => {
      capturePress(pressLabel(grant.name), "grant_row");
      navigation.navigate("GrantDetail", { grantId: grant.id });
    },
    [capturePress, navigation],
  );

  const renderGrant = useCallback(
    ({ item }: { item: Grant }) => {
      const status = eligibilityMap.get(item.id);
      return (
        <View style={{ paddingHorizontal: 16, marginBottom: 12 }}>
          <Pressable
            onPress={() => handleGrantPress(item)}
            style={{
              borderRadius: 16,
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              borderWidth: 1,
              borderColor: "rgba(255, 255, 255, 0.5)",
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(60px)",
                WebkitBackdropFilter: "blur(60px)",
              }),
              overflow: "hidden",
            }}
          >
            <View style={{ padding: 18 }}>
              {/* Header */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <Image
                  source={{ uri: item.favicon }}
                  style={
                    {
                      width: 22,
                      height: 22,
                      borderRadius: 4,
                      opacity: 0.55,
                      ...(Platform.OS === "web" && {
                        filter: "grayscale(1)",
                      }),
                    } as Record<string, unknown>
                  }
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 15,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                    flex: 1,
                  }}
                  numberOfLines={1}
                >
                  {item.name}
                </Text>
                {status && <EligibilityBadge status={status} />}
                <ChevronRight
                  size={14}
                  color={EMBER.colors.textFaint}
                  strokeWidth={1.5}
                />
              </View>

              {/* Description */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 19,
                }}
                numberOfLines={2}
              >
                {item.shortDescription}
              </Text>
            </View>

            {/* Info strip */}
            <View
              style={{
                flexDirection: "row",
                backgroundColor: "rgba(255, 255, 255, 0.12)",
                borderTopWidth: 1,
                borderTopColor: "rgba(255, 255, 255, 0.25)",
              }}
            >
              <View
                style={{
                  flex: 1,
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    marginBottom: 4,
                  }}
                >
                  <Trophy
                    size={10}
                    color={EMBER.colors.textFaint}
                    strokeWidth={1.5}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 10,
                      color: EMBER.colors.textFaint,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Award
                  </Text>
                </View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "600",
                    color: EMBER.colors.warm,
                  }}
                >
                  {item.awardAmount}
                </Text>
              </View>
              <View
                style={{
                  width: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.25)",
                  marginVertical: 10,
                }}
              />
              <View
                style={{
                  flex: 1.4,
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    marginBottom: 4,
                  }}
                >
                  <Users
                    size={10}
                    color={EMBER.colors.textFaint}
                    strokeWidth={1.5}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 10,
                      color: EMBER.colors.textFaint,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Eligibility
                  </Text>
                </View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "500",
                    color: EMBER.colors.text,
                  }}
                  numberOfLines={1}
                >
                  {formatEligibilitySummary(item)}
                </Text>
              </View>
              <View
                style={{
                  width: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.25)",
                  marginVertical: 10,
                }}
              />
              <View
                style={{
                  flex: 0.9,
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    marginBottom: 4,
                  }}
                >
                  <Calendar
                    size={10}
                    color={EMBER.colors.textFaint}
                    strokeWidth={1.5}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 10,
                      color: EMBER.colors.textFaint,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Status
                  </Text>
                </View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "500",
                    color:
                      item.applicationStatus === "open"
                        ? EMBER.colors.green
                        : item.applicationStatus === "upcoming"
                          ? EMBER.colors.gold
                          : EMBER.colors.textFaint,
                  }}
                >
                  {item.applicationStatus === "open"
                    ? "Open"
                    : item.applicationStatus === "upcoming"
                      ? "Upcoming"
                      : "Closed"}
                </Text>
              </View>
            </View>
          </Pressable>
        </View>
      );
    },
    [eligibilityMap, handleGrantPress],
  );

  const ListHeader = (
    <View
      style={{
        paddingHorizontal: 16,
        paddingTop: SCREEN_PADDING_TOP,
      }}
    >
      {/* Back button */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(
              route.params?.returnTab ? t("you.shared.back") : "Costs",
            ),
            "back",
          );
          handleBack();
        }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
          marginBottom: 8,
        }}
      >
        <ChevronLeft size={18} color={EMBER.colors.warm} strokeWidth={1.5} />
        <Text
          className="font-dm-sans"
          style={{ fontSize: 14, color: EMBER.colors.warm }}
        >
          {route.params?.returnTab ? t("you.shared.back") : "Costs"}
        </Text>
      </Pressable>

      {/* Title */}
      <Text
        className="font-dm-sans"
        style={
          {
            fontSize: 38,
            fontWeight: "700",
            color: "#3B1A08",
            lineHeight: 42,
            letterSpacing: -0.5,
            ...(Platform.OS === "web" && {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
              backgroundSize: "200px 200px, 100% 100%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }),
          } as Record<string, unknown>
        }
      >
        Grants
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          color: EMBER.colors.textLight,
          marginTop: 2,
        }}
      >
        {filteredGrants.length} grants and financial assistance for fertility
        treatment
      </Text>

      {/* Search bar + filter button */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          marginTop: 18,
        }}
      >
        <MoonSearchInput
          placeholder="Search grants..."
          value={filters.search ?? ""}
          onChangeText={(text) =>
            setFilters((prev) => ({ ...prev, search: text }))
          }
          onClear={() => setFilters((prev) => ({ ...prev, search: "" }))}
          tone="glass"
          style={{ flex: 1 }}
        />
        <GrantsFilterModal
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      </View>

      {/* Quick Filter Chips */}
      <FlatList
        horizontal
        data={QUICK_FILTERS}
        keyExtractor={(item) => item.label}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 8, paddingVertical: 14 }}
        renderItem={({ item: { label } }) => (
          <MoonFilterChip
            label={label}
            selected={activeChip === label}
            onPress={() => handleChipPress(label)}
            tone="glass"
            showCheck={false}
          />
        )}
      />
    </View>
  );

  const ListEmpty = (
    <View
      style={{
        alignItems: "center",
        paddingVertical: 40,
        paddingHorizontal: 16,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          color: EMBER.colors.textLight,
          textAlign: "center",
        }}
      >
        No grants found matching your search.{"\n"}Try adjusting your filters.
      </Text>
    </View>
  );

  if (region !== "US") return <RegionGatePlaceholder />;

  return (
    <GlassScreenLayout>
      <FlatList
        data={filteredGrants}
        keyExtractor={(item) => item.id}
        renderItem={renderGrant}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
      />
    </GlassScreenLayout>
  );
}

function formatEligibilitySummary(grant: Grant): string {
  const parts: string[] = [];
  const { eligibility } = grant;

  if (eligibility.location === "national") {
    parts.push("U.S.");
  } else {
    parts.push(eligibility.location.join(", "));
  }

  if (eligibility.ageMax) {
    parts.push(`Under ${eligibility.ageMax}`);
  }

  if (eligibility.specialGroups) {
    const labels: Record<string, string> = {
      veterans: "Veterans",
      lgbtq: "LGBTQ+",
      jewish: "Jewish",
    };
    parts.push(labels[eligibility.specialGroups] ?? eligibility.specialGroups);
  }

  return parts.join(" · ");
}
